/**
 * Checking and repairing RIS files — the tagged format EndNote, Zotero, Mendeley and
 * most databases exchange references in.
 *
 * The rules are Thomson Reuters' "RIS" Format Documentation (ResearchSoft, 2009): every
 * line starts with a six-character tag — an upper-case letter, an upper-case letter or
 * digit, two spaces, a hyphen, a space — `TY` comes first in a record and `ER` last,
 * lines end in CR LF, and a long value may wrap onto the next line. The tag and type
 * lists are Reference Manager's (refman.com, 2011–2012). Tags from its older
 * specification — JF, JO, JA, T1, A1, Y1, EP, N2 and the rest — are still accepted,
 * because every major importer still reads them.
 *
 * Where an importer departs from the specification, the check follows the importer,
 * because that is what happens to the reader's references: Zotero reads `JO` as the
 * journal *abbreviation*, so a full journal title there is reported.
 */

import { scanDois } from "@/lib/tool-kit/identifiers/doi-text";

/* ------------------------------------------------------------ vocabulary */

/** Tags in the current specification. */
const SPEC_TAGS = [
  "TY",
  "ER",
  "AU",
  "A2",
  "A3",
  "A4",
  "AB",
  "AD",
  "AN",
  "C1",
  "C2",
  "C3",
  "C4",
  "C5",
  "C6",
  "C7",
  "C8",
  "CA",
  "CN",
  "CT",
  "CY",
  "DA",
  "DB",
  "DO",
  "DP",
  "ET",
  "IS",
  "J2",
  "KW",
  "L1",
  "L4",
  "LA",
  "LB",
  "M1",
  "M2",
  "M3",
  "N1",
  "NV",
  "OP",
  "PB",
  "PY",
  "RI",
  "RN",
  "RP",
  "SE",
  "SN",
  "SP",
  "ST",
  "SV",
  "T2",
  "T3",
  "TA",
  "TI",
  "TT",
  "UR",
  "VL",
  "Y2",
];

/** Tags from Reference Manager's earlier specification that importers still read. */
const LEGACY_TAGS = [
  "A1",
  "AV",
  "BT",
  "CP",
  "ED",
  "EP",
  "ID",
  "J1",
  "JA",
  "JF",
  "JO",
  "L2",
  "L3",
  "N2",
  "T1",
  "U1",
  "U2",
  "U3",
  "U4",
  "U5",
  "Y1",
];

export const RIS_TAGS: ReadonlySet<string> = new Set([
  ...SPEC_TAGS,
  ...LEGACY_TAGS,
]);

/** Reference types (`TY` values). WEB is not in the specification but EndNote writes it. */
export const RIS_TYPES: ReadonlySet<string> = new Set([
  "ABST",
  "ADVS",
  "AGGR",
  "ANCIENT",
  "ART",
  "BILL",
  "BLOG",
  "BOOK",
  "CASE",
  "CHAP",
  "CHART",
  "CLSWK",
  "COMP",
  "CONF",
  "CPAPER",
  "CTLG",
  "DATA",
  "DBASE",
  "DICT",
  "EBOOK",
  "ECHAP",
  "EDBOOK",
  "EJOUR",
  "ELEC",
  "ENCYC",
  "EQUA",
  "FIGURE",
  "GEN",
  "GOVDOC",
  "GRANT",
  "GRNT",
  "HEAR",
  "ICOMM",
  "INPR",
  "JFULL",
  "JOUR",
  "LEGAL",
  "MANSCPT",
  "MAP",
  "MGZN",
  "MPCT",
  "MULTI",
  "MUSIC",
  "NEWS",
  "PAMP",
  "PAT",
  "PCOMM",
  "RPRT",
  "SER",
  "SLIDE",
  "SOUND",
  "STAND",
  "STAT",
  "THES",
  "UNBILL",
  "UNPB",
  "UNPD",
  "VIDEO",
  "WEB",
]);

/** What a reference of each type needs before a style can cite it. */
export type FieldGroup =
  | "title"
  | "author"
  | "creator"
  | "journal"
  | "bookTitle"
  | "conference"
  | "year"
  | "publisher"
  | "url";

export const FIELD_GROUP_TAGS: Record<FieldGroup, readonly string[]> = {
  title: ["TI", "T1", "CT"],
  author: ["AU", "A1"],
  creator: ["AU", "A1", "A2", "A3", "ED"],
  journal: ["T2", "JF", "JO", "JA", "J1", "J2"],
  bookTitle: ["T2", "BT"],
  conference: ["T2", "C3", "BT", "JO"],
  year: ["PY", "Y1", "DA"],
  publisher: ["PB"],
  url: ["UR", "L2"],
};

const ARTICLE: FieldGroup[] = ["title", "author", "journal", "year"];
const WHOLE_BOOK: FieldGroup[] = ["title", "creator", "year", "publisher"];
const SECTION: FieldGroup[] = ["title", "author", "bookTitle", "year"];
const WEB_PAGE: FieldGroup[] = ["title", "url"];

export const NEEDED_FIELDS: Record<string, readonly FieldGroup[]> = {
  JOUR: ARTICLE,
  EJOUR: ARTICLE,
  ABST: ARTICLE,
  INPR: ["title", "author", "journal"],
  JFULL: ["journal", "year"],
  MGZN: ["title", "journal", "year"],
  NEWS: ["title", "journal", "year"],
  BOOK: WHOLE_BOOK,
  EBOOK: WHOLE_BOOK,
  EDBOOK: WHOLE_BOOK,
  CHAP: SECTION,
  ECHAP: SECTION,
  CPAPER: ["title", "author", "conference", "year"],
  CONF: ["title", "year"],
  THES: ["title", "author", "year", "publisher"],
  RPRT: ["title", "year"],
  DATA: ["title", "year"],
  ELEC: WEB_PAGE,
  WEB: WEB_PAGE,
  BLOG: WEB_PAGE,
};
const DEFAULT_NEEDS: readonly FieldGroup[] = ["title"];
const NEEDS_BY_TYPE = new Map(Object.entries(NEEDED_FIELDS));

/** Periodical types, for which Zotero reads `JO` as the journal abbreviation. */
const PERIODICAL_TYPES = new Set([
  "JOUR",
  "EJOUR",
  "JFULL",
  "INPR",
  "ABST",
  "MGZN",
  "NEWS",
]);
const PERSON_TAGS = new Set(["AU", "A1", "A2", "A3", "A4", "ED"]);
const DATE_TAG_LIST = ["PY", "Y1", "DA"];
const DATE_TAGS = new Set(DATE_TAG_LIST);
const DOI_SOURCE_TAGS = ["DO", "M3", "N1", "UR", "L3"];

/* -------------------------------------------------------------- parsing */

const BOM = "\uFEFF";
const RE_LINE = /([^\r\n]*)(\r\n|\n|\r)/g;
const RE_BOM_ALL = /\uFEFF/g;
const RE_TAG_EXACT = /^([A-Z][A-Z0-9]) {2}- (.*)$/;
const RE_TAG_BARE_DASH = /^([A-Z][A-Z0-9]) {2}-$/;
// Indented, one space or three, a tab, no space after the hyphen, an en dash for the
// hyphen. A space or the line end must sit on one side of the dash, so prose such as
// "DA-based methods" in a wrapped abstract is not mistaken for a tag.
const RE_TAG_LOOSE =
  /^\s*([A-Z][A-Z0-9])(?:[ \t]+[-\u2010-\u2014][ \t]*|[ \t]*[-\u2010-\u2014](?:[ \t]+|$))(.*)$/;

export type RisLineKind = "tag" | "continuation" | "blank" | "outside";

export interface RisLine {
  /** 1-based, as an editor counts. */
  number: number;
  /** The line as written, without its line ending. */
  raw: string;
  /** "\r\n", "\n", "\r", or "" on a last line with none. */
  eol: string;
  /** The line with byte-order marks removed — what is parsed. */
  text: string;
  /** Byte-order marks inside the line (not counting one that opens the file). */
  strayBoms: number;
  kind: RisLineKind;
  tag?: string;
  value?: string;
  /** A tag line not in the `XX  - value` form: wrong spacing, a tab, an en dash, indented. */
  malformed?: boolean;
  /** `XX  -` with the final space missing. */
  bareDash?: boolean;
  /** 1-based record the line belongs to. */
  record?: number;
}

export interface RisField {
  tag: string;
  /** Continuation lines joined on with a space. */
  value: string;
  /** Position in `lines` (0-based). */
  at: number;
}

export interface RisRecord {
  /** 1-based. */
  index: number;
  /** Positions in `lines` (0-based) of the record's first and last non-blank line. */
  first: number;
  last: number;
  /** The `TY` value, trimmed — null when the record has no TY line. */
  type: string | null;
  hasEnd: boolean;
  fields: RisField[];
}

export interface RisParse {
  lines: RisLine[];
  records: RisRecord[];
  /** The text opened with a byte-order mark. */
  leadingBom: boolean;
  endings: { crlf: number; lf: number; cr: number };
}

function splitLines(text: string): { raw: string; eol: string }[] {
  const lines: { raw: string; eol: string }[] = [];
  let end = 0;
  for (const match of text.matchAll(RE_LINE)) {
    lines.push({ raw: match[1], eol: match[2] });
    end = match.index + match[0].length;
  }
  if (end < text.length) {
    lines.push({ raw: text.slice(end), eol: "" });
  }
  return lines;
}

function classify(line: RisLine): void {
  if (line.text.trim() === "") {
    line.kind = "blank";
    return;
  }
  const exact = RE_TAG_EXACT.exec(line.text);
  if (exact) {
    Object.assign(line, { kind: "tag", tag: exact[1], value: exact[2] });
    return;
  }
  const bare = RE_TAG_BARE_DASH.exec(line.text);
  if (bare) {
    Object.assign(line, {
      kind: "tag",
      tag: bare[1],
      value: "",
      bareDash: true,
    });
    return;
  }
  const loose = RE_TAG_LOOSE.exec(line.text);
  if (loose && RIS_TAGS.has(loose[1])) {
    Object.assign(line, {
      kind: "tag",
      tag: loose[1],
      value: loose[2],
      malformed: true,
    });
    return;
  }
  line.kind = "continuation";
}

function readLines(text: string): Pick<RisParse, "lines" | "leadingBom"> {
  const leadingBom = text.startsWith(BOM);
  const lines = splitLines(leadingBom ? text.slice(1) : text).map(
    ({ raw, eol }, index): RisLine => {
      const stray = raw.match(RE_BOM_ALL)?.length ?? 0;
      const line: RisLine = {
        number: index + 1,
        raw,
        eol,
        text: stray > 0 ? raw.replace(RE_BOM_ALL, "") : raw,
        strayBoms: stray,
        kind: "blank",
      };
      classify(line);
      return line;
    }
  );
  return { lines, leadingBom };
}

interface GroupState {
  records: RisRecord[];
  open: RisRecord | null;
  /** Position of the last line that belonged to a record. */
  lastContent: number;
}

function closeRecord(state: GroupState, hasEnd: boolean): void {
  if (state.open) {
    state.open.last = state.lastContent;
    state.open.hasEnd = hasEnd;
    state.open = null;
  }
}

function openRecord(
  state: GroupState,
  type: string | null,
  at: number
): RisRecord {
  const record: RisRecord = {
    index: state.records.length + 1,
    first: at,
    last: at,
    type,
    hasEnd: false,
    fields: [],
  };
  state.records.push(record);
  state.open = record;
  return record;
}

/** A wrapped value continues the field above; outside a record it is stray text. */
function addContinuation(state: GroupState, line: RisLine, at: number): void {
  const field = state.open?.fields.at(-1);
  if (!(state.open && field)) {
    line.kind = "outside";
    return;
  }
  field.value = `${field.value} ${line.text.trim()}`;
  line.record = state.open.index;
  state.lastContent = at;
}

function addTag(state: GroupState, line: RisLine, at: number): void {
  const tag = line.tag ?? "";
  if (tag === "TY") {
    // A TY inside an open record starts the next one: the ER was left out.
    closeRecord(state, false);
  }
  if (tag === "ER" && !state.open) {
    line.kind = "outside";
    return;
  }
  const record =
    state.open ??
    openRecord(state, tag === "TY" ? (line.value ?? "").trim() : null, at);
  line.record = record.index;
  state.lastContent = at;
  if (tag === "ER") {
    closeRecord(state, true);
    return;
  }
  record.fields.push({ tag, value: line.value ?? "", at });
}

/** Group the lines into records: a record runs from TY (or its first tag) to ER. */
function groupRecords(lines: RisLine[]): RisRecord[] {
  const state: GroupState = { records: [], open: null, lastContent: -1 };
  for (const [at, line] of lines.entries()) {
    if (line.kind === "continuation") {
      addContinuation(state, line, at);
    } else if (line.kind === "tag") {
      addTag(state, line, at);
    }
  }
  closeRecord(state, false);
  return state.records;
}

/** Read RIS text into lines and records. Never throws: anything unreadable is reported. */
export function parseRis(text: string): RisParse {
  const { lines, leadingBom } = readLines(text);
  const endings = { crlf: 0, lf: 0, cr: 0 };
  for (const line of lines) {
    if (line.eol === "\r\n") {
      endings.crlf += 1;
    } else if (line.eol === "\n") {
      endings.lf += 1;
    } else if (line.eol === "\r") {
      endings.cr += 1;
    }
  }
  return { lines, records: groupRecords(lines), leadingBom, endings };
}

/* --------------------------------------------------------------- checks */

export type RisSeverity = "error" | "warning" | "info";

export type RisFixKind =
  | "normalize"
  | "add-end"
  | "move-jo"
  | "drop-duplicates";

export type RisIssueCode =
  | "no-records"
  | "bom-start"
  | "bom-stray"
  | "mojibake"
  | "replacement-char"
  | "line-endings-mixed"
  | "line-endings-lf"
  | "line-endings-cr"
  | "malformed-tag"
  | "unknown-tag"
  | "outside-record"
  | "missing-ty"
  | "missing-er"
  | "unknown-type"
  | "missing-fields"
  | "no-year"
  | "year-format"
  | "author-list"
  | "er-value"
  | "jo-full-title"
  | "jo-repeats-title"
  | "jo-looks-full"
  | "duplicate-exact"
  | "duplicate-doi"
  | "duplicate-title";

export interface RisIssue {
  code: RisIssueCode;
  severity: RisSeverity;
  /** 1-based record number. */
  record?: number;
  /** 1-based line number. */
  line?: number;
  /** Values for the message — the tag, the type, the other record, the fields missing. */
  detail: Record<string, string>;
  /** The repair the cleaner can make, when there is a safe one. */
  fix?: RisFixKind;
}

// UTF-8 read as Windows-1252: "é" arrives as "Ã©", "’" as "â€™", a no-break space as
// "Â" plus a space. The second character is what a UTF-8 continuation byte becomes.
const CONTINUATION =
  "\\u0080-\\u00bf\\u0152\\u0153\\u0160\\u0161\\u0178\\u017d\\u017e\\u0192\\u02c6\\u02dc\\u2013\\u2014\\u2018-\\u201e\\u2020-\\u2022\\u2026\\u2030\\u2039\\u203a\\u20ac\\u2122";
const RE_MOJIBAKE = new RegExp(
  `[\\u00c2\\u00c3][${CONTINUATION}]|\\u00e2\\u20ac[${CONTINUATION}]`
);
const RE_REPLACEMENT = /\uFFFD/;
const RE_YEAR = /(?:^|\D)(\d{4})(?!\d)/;
const RE_YEAR_ONLY = /^\d{4}$/;
const RE_FOLD = /[^\p{L}\p{N}]+/gu;
const RE_FUNCTION_WORD =
  /\b(?:of|the|and|for|in|on|de|des|du|la|le|les|und|für|der|die|das|di|del|della)\b/;
const RE_STARTS_THE = /^The\s/;
/**
 * Words NLM abbreviates whenever they sit in a journal name of two or more words —
 * each survives unabbreviated in under 2% of the multi-word abbreviations in its
 * journal list ("Physics" → Phys, "Journal" → J). Spelled out, they mark a full title.
 */
const RE_SPELLED_OUT =
  /\b(?:journals?|reviews?|international|research|proceedings|transactions|annals|archives|american|british|european|quarterly|bulletin|letters|society|association|studies|medicine|medical|clinical|physics|physical|chemistry|chemical|biology|biological|communications|neuroscience|psychology|genetics|materials|engineering|sciences?|applied|environmental|molecular|experimental|annual|national|education|nursing|surgery|neurology|physiology|mathematics|technology|management|behavioral|cognitive|developmental)\b/i;
const RE_SPACE = /\s/;

function fold(text: string): string {
  return text.toLowerCase().replace(RE_FOLD, " ").trim();
}

/** A full journal title rather than an abbreviation ("J Neurosurg", "J. Neurosurg."). */
export function looksLikeFullTitle(value: string): boolean {
  const trimmed = value.trim();
  if (RE_FUNCTION_WORD.test(trimmed) || RE_STARTS_THE.test(trimmed)) {
    return true;
  }
  // A one-word name ("Nature", "Science") reads the same either way.
  return (
    RE_SPACE.test(trimmed) &&
    !trimmed.includes(".") &&
    RE_SPELLED_OUT.test(trimmed)
  );
}

function issue(
  code: RisIssueCode,
  severity: RisSeverity,
  where: { record?: number; line?: number },
  detail: Record<string, string> = {},
  fix?: RisFixKind
): RisIssue {
  return { code, severity, ...where, detail, fix };
}

function fileIssues(parse: RisParse): RisIssue[] {
  const issues: RisIssue[] = [];
  if (parse.leadingBom) {
    issues.push(issue("bom-start", "info", {}));
  }
  const { crlf, lf, cr } = parse.endings;
  const kinds = [crlf, lf, cr].filter((count) => count > 0).length;
  if (kinds > 1) {
    issues.push(issue("line-endings-mixed", "warning", {}, {}, "normalize"));
  } else if (cr > 0) {
    issues.push(issue("line-endings-cr", "warning", {}, {}, "normalize"));
  } else if (lf > 0) {
    issues.push(issue("line-endings-lf", "info", {}, {}, "normalize"));
  }
  if (parse.records.length === 0) {
    issues.push(issue("no-records", "error", {}));
  }
  return issues;
}

function lineIssues(line: RisLine): RisIssue[] {
  const where = { record: line.record, line: line.number };
  const issues: RisIssue[] = [];
  if (line.strayBoms > 0) {
    issues.push(issue("bom-stray", "warning", where, {}, "normalize"));
  }
  const mojibake = RE_MOJIBAKE.exec(line.text);
  if (mojibake) {
    issues.push(issue("mojibake", "warning", where, { sample: mojibake[0] }));
  }
  if (RE_REPLACEMENT.test(line.text)) {
    issues.push(issue("replacement-char", "warning", where));
  }
  if (line.kind === "outside") {
    issues.push(
      issue("outside-record", "warning", where, { text: line.text.trim() })
    );
  }
  if (line.kind !== "tag") {
    return issues;
  }
  const tag = line.tag ?? "";
  if (line.malformed) {
    issues.push(
      issue(
        "malformed-tag",
        "error",
        where,
        { tag, text: line.text },
        "normalize"
      )
    );
  }
  if (!RIS_TAGS.has(tag)) {
    issues.push(issue("unknown-tag", "warning", where, { tag }));
  }
  if (tag === "ER" && (line.value ?? "").trim() !== "") {
    issues.push(issue("er-value", "warning", where));
  }
  return issues;
}

function valuesOf(record: RisRecord, tags: readonly string[]): string[] {
  return record.fields
    .filter((field) => tags.includes(field.tag) && field.value.trim() !== "")
    .map((field) => field.value.trim());
}

function typeIssues(record: RisRecord, lines: RisLine[]): RisIssue[] {
  const where = { record: record.index, line: lines[record.first].number };
  if (record.type === null) {
    return [issue("missing-ty", "error", where)];
  }
  if (RIS_TYPES.has(record.type)) {
    return [];
  }
  const upper = record.type.toUpperCase();
  return [
    issue("unknown-type", "warning", where, {
      type: record.type,
      suggestion: RIS_TYPES.has(upper) ? upper : "",
    }),
  ];
}

function fieldIssues(record: RisRecord, lines: RisLine[]): RisIssue[] {
  const where = { record: record.index, line: lines[record.first].number };
  const needs = NEEDS_BY_TYPE.get(record.type ?? "") ?? DEFAULT_NEEDS;
  const missing = needs.filter(
    (group) => valuesOf(record, FIELD_GROUP_TAGS[group]).length === 0
  );
  return missing.length > 0
    ? [
        issue("missing-fields", "warning", where, {
          fields: missing.join(","),
          type: record.type ?? "",
        }),
      ]
    : [];
}

function valueIssues(record: RisRecord, lines: RisLine[]): RisIssue[] {
  const issues: RisIssue[] = [];
  for (const field of record.fields) {
    const where = { record: record.index, line: lines[field.at].number };
    const value = field.value.trim();
    if (PERSON_TAGS.has(field.tag) && value.includes(";")) {
      issues.push(issue("author-list", "warning", where, { tag: field.tag }));
    }
    if (!DATE_TAGS.has(field.tag) || value === "") {
      continue;
    }
    if (!RE_YEAR.test(value)) {
      issues.push(
        issue("no-year", "warning", where, { tag: field.tag, value })
      );
    } else if (field.tag === "PY" && !RE_YEAR_ONLY.test(value)) {
      issues.push(issue("year-format", "info", where, { value }));
    }
  }
  return issues;
}

function journalIssues(record: RisRecord, lines: RisLine[]): RisIssue[] {
  if (!PERIODICAL_TYPES.has(record.type ?? "")) {
    return [];
  }
  const jo = record.fields.find(
    (field) => field.tag === "JO" && field.value.trim() !== ""
  );
  if (!jo) {
    return [];
  }
  const where = { record: record.index, line: lines[jo.at].number };
  const value = jo.value.trim();
  const container = valuesOf(record, ["T2", "JF"])[0];
  if (container !== undefined && fold(container) === fold(value)) {
    return [issue("jo-repeats-title", "info", where, { value }, "move-jo")];
  }
  if (!looksLikeFullTitle(value)) {
    return [];
  }
  if (container === undefined) {
    return [issue("jo-full-title", "warning", where, { value }, "move-jo")];
  }
  return [issue("jo-looks-full", "warning", where, { value, container })];
}

/** The text two records must share, line for line, to be exact duplicates. */
function recordKey(record: RisRecord): string {
  return record.fields
    .map((field) => `${field.tag}\u0000${field.value.trim()}`)
    .join("\n");
}

function recordDoi(record: RisRecord): string {
  for (const value of valuesOf(record, DOI_SOURCE_TAGS)) {
    const found = scanDois(value).dois[0];
    if (found) {
      return found.doi.toLowerCase();
    }
  }
  return "";
}

/** Folded title + year + first author: "the same work" for the likely-duplicate check. */
function workKey(record: RisRecord): string {
  const title = fold(valuesOf(record, FIELD_GROUP_TAGS.title)[0] ?? "");
  const year = RE_YEAR.exec(valuesOf(record, DATE_TAG_LIST).join(" "))?.[1];
  if (title === "" || !year) {
    return "";
  }
  const author = fold((valuesOf(record, ["AU", "A1"])[0] ?? "").split(",")[0]);
  return `${title}|${year}|${author}`;
}

function duplicateIssues(records: RisRecord[], lines: RisLine[]): RisIssue[] {
  const issues: RisIssue[] = [];
  const firstByKey = new Map<string, number>();
  const firstByDoi = new Map<string, number>();
  const firstByWork = new Map<string, number>();
  for (const record of records) {
    const where = { record: record.index, line: lines[record.first].number };
    const key = recordKey(record);
    const exact = firstByKey.get(key);
    if (exact !== undefined) {
      issues.push(
        issue(
          "duplicate-exact",
          "warning",
          where,
          { of: String(exact) },
          "drop-duplicates"
        )
      );
      continue;
    }
    firstByKey.set(key, record.index);
    const doi = recordDoi(record);
    const work = workKey(record);
    const sameDoi = doi ? firstByDoi.get(doi) : undefined;
    const sameWork = work ? firstByWork.get(work) : undefined;
    if (sameDoi !== undefined) {
      issues.push(
        issue("duplicate-doi", "warning", where, { of: String(sameDoi), doi })
      );
    } else if (sameWork !== undefined) {
      issues.push(
        issue("duplicate-title", "warning", where, { of: String(sameWork) })
      );
    }
    if (doi && !firstByDoi.has(doi)) {
      firstByDoi.set(doi, record.index);
    }
    if (work && !firstByWork.has(work)) {
      firstByWork.set(work, record.index);
    }
  }
  return issues;
}

const SEVERITY_ORDER: Record<RisSeverity, number> = {
  error: 0,
  warning: 1,
  info: 2,
};

/** Every problem in a parsed file, in line order (file-wide notes first). */
export function checkRis(parse: RisParse): RisIssue[] {
  const { lines, records } = parse;
  const issues = [...fileIssues(parse), ...lines.flatMap(lineIssues)];
  for (const record of records) {
    issues.push(
      ...typeIssues(record, lines),
      ...fieldIssues(record, lines),
      ...valueIssues(record, lines),
      ...journalIssues(record, lines)
    );
    if (!record.hasEnd) {
      issues.push(
        issue(
          "missing-er",
          "error",
          { record: record.index, line: lines[record.last].number },
          {},
          "add-end"
        )
      );
    }
  }
  issues.push(...duplicateIssues(records, lines));
  return issues.sort(
    (a, b) =>
      (a.line ?? 0) - (b.line ?? 0) ||
      SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]
  );
}

/* --------------------------------------------------------------- repairs */

export interface RisFixOptions {
  /** Tag lines into `XX  - value`, stray byte-order marks out, every line ending CR LF. */
  normalize: boolean;
  /** Close each record that has no ER. */
  addEnd: boolean;
  /** A full journal title from JO to T2, or drop a JO that repeats T2. */
  moveJo: boolean;
  /** Remove records that repeat an earlier one exactly. */
  dropDuplicates: boolean;
}

export type RisChangeKind =
  | "spacing"
  | "bom"
  | "line-endings"
  | "end-added"
  | "jo-moved"
  | "jo-dropped"
  | "duplicate-dropped";

export interface RisChange {
  kind: RisChangeKind;
  record?: number;
  /** 1-based line in the original text. */
  line?: number;
  before: string;
  after: string;
  /** "of" (the record a duplicate repeats), "to" (its last line), "count". */
  detail: Record<string, string>;
}

export interface RisCleanResult {
  text: string;
  changes: RisChange[];
  /** Records in the cleaned file. */
  records: number;
}

interface Plan {
  /** Record indices to remove, with the record each one repeats. */
  drop: Map<number, string>;
  /** Positions of JO lines to retag as T2, or to remove. */
  jo: Map<number, "move" | "drop">;
  /** Positions after which an ER line goes in, with the record it closes. */
  endAfter: Map<number, number>;
}

/** Record numbers to remove, each with the record it repeats. */
function planDrops(
  issues: RisIssue[],
  options: RisFixOptions
): Map<number, string> {
  const drop = new Map<number, string>();
  if (!options.dropDuplicates) {
    return drop;
  }
  for (const found of issues) {
    if (found.code === "duplicate-exact" && found.record !== undefined) {
      drop.set(found.record, found.detail.of);
    }
  }
  return drop;
}

/** The JO repair for one issue, if the issue is a JO one: retag it, or drop it. */
function joAction(found: RisIssue): "move" | "drop" | undefined {
  if (found.code === "jo-full-title") {
    return "move";
  }
  return found.code === "jo-repeats-title" ? "drop" : undefined;
}

function planFixes(
  parse: RisParse,
  issues: RisIssue[],
  options: RisFixOptions
): Plan {
  const plan: Plan = {
    drop: planDrops(issues, options),
    jo: new Map(),
    endAfter: new Map(),
  };
  const byIndex = new Map(
    parse.records.map((record) => [record.index, record])
  );
  for (const found of issues) {
    const record = byIndex.get(found.record ?? 0);
    if (!record || plan.drop.has(record.index)) {
      continue;
    }
    if (options.addEnd && found.code === "missing-er") {
      plan.endAfter.set(record.last, record.index);
    }
    const action = options.moveJo ? joAction(found) : undefined;
    const jo = record.fields.find((field) => field.tag === "JO");
    if (action && jo) {
      plan.jo.set(jo.at, action);
    }
  }
  return plan;
}

/** The positions a dropped record takes up, with the blank lines that follow it. */
function droppedPositions(parse: RisParse, plan: Plan): Set<number> {
  const positions = new Set<number>();
  for (const record of parse.records) {
    if (!plan.drop.has(record.index)) {
      continue;
    }
    let at = record.first;
    while (
      at < parse.lines.length &&
      (at <= record.last || parse.lines[at].kind === "blank")
    ) {
      positions.add(at);
      at += 1;
    }
  }
  return positions;
}

function tagLine(tag: string, value: string): string {
  return `${tag}  - ${value}`;
}

/** The dominant line ending, for lines the cleaner inserts when it keeps the original ones. */
function dominantEol(parse: RisParse): string {
  const { crlf, lf, cr } = parse.endings;
  if (lf > crlf && lf >= cr) {
    return "\n";
  }
  return cr > crlf ? "\r" : "\r\n";
}

interface Writer {
  out: string[];
  changes: RisChange[];
  eol: (line: RisLine) => string;
}

/** One line's text after the repairs that apply to it, logging each change. */
function repairLine(
  line: RisLine,
  action: "move" | "drop" | undefined,
  options: RisFixOptions,
  writer: Writer
): string {
  const where = { record: line.record, line: line.number, detail: {} };
  if (action === "move") {
    const after = tagLine("T2", (line.value ?? "").trim());
    writer.changes.push({
      kind: "jo-moved",
      ...where,
      before: line.raw,
      after,
    });
    return after;
  }
  if (!options.normalize) {
    return line.raw;
  }
  let text = line.text;
  if (line.strayBoms > 0) {
    writer.changes.push({
      kind: "bom",
      ...where,
      before: line.raw,
      after: text,
    });
  }
  if (line.kind === "tag" && (line.malformed || line.bareDash)) {
    text = tagLine(line.tag ?? "", line.value ?? "");
    writer.changes.push({
      kind: "spacing",
      ...where,
      before: line.text,
      after: text,
    });
  }
  return text;
}

/** Positions of a JO line being dropped and the continuation lines that belong to it. */
function joDropPositions(parse: RisParse, plan: Plan): Set<number> {
  const positions = new Set<number>();
  for (const [at, action] of plan.jo) {
    if (action !== "drop") {
      continue;
    }
    positions.add(at);
    let next = at + 1;
    while (parse.lines[next]?.kind === "continuation") {
      positions.add(next);
      next += 1;
    }
  }
  return positions;
}

function logDrops(parse: RisParse, plan: Plan, changes: RisChange[]): void {
  for (const record of parse.records) {
    const of = plan.drop.get(record.index);
    if (of !== undefined) {
      changes.push({
        kind: "duplicate-dropped",
        record: record.index,
        line: parse.lines[record.first].number,
        before: parse.lines[record.first].text,
        after: "",
        detail: { of, to: String(parse.lines[record.last].number) },
      });
    }
  }
  for (const [at, action] of plan.jo) {
    if (action === "drop") {
      const line = parse.lines[at];
      changes.push({
        kind: "jo-dropped",
        record: line.record,
        line: line.number,
        before: line.raw,
        after: "",
        detail: {},
      });
    }
  }
}

/**
 * Apply the safe repairs and report every change. Nothing is guessed: a missing TY, an
 * unknown tag or a likely duplicate is reported by `checkRis` and left for the reader.
 */
export function cleanRis(
  parse: RisParse,
  issues: RisIssue[],
  options: RisFixOptions
): RisCleanResult {
  const plan = planFixes(parse, issues, options);
  const skip = new Set([
    ...droppedPositions(parse, plan),
    ...joDropPositions(parse, plan),
  ]);
  const fallbackEol = dominantEol(parse);
  const writer: Writer = {
    out: [],
    changes: [],
    eol: (line) => (options.normalize ? "\r\n" : line.eol || fallbackEol),
  };
  logDrops(parse, plan, writer.changes);
  for (const [at, line] of parse.lines.entries()) {
    if (!skip.has(at)) {
      const text = repairLine(line, plan.jo.get(at), options, writer);
      writer.out.push(text, writer.eol(line));
    }
    const closes = plan.endAfter.get(at);
    if (closes !== undefined) {
      writer.changes.push({
        kind: "end-added",
        record: closes,
        line: line.number,
        before: "",
        after: tagLine("ER", ""),
        detail: {},
      });
      writer.out.push(
        tagLine("ER", ""),
        options.normalize ? "\r\n" : fallbackEol
      );
    }
  }
  const { lf, cr } = parse.endings;
  const lastEol = parse.lines.at(-1)?.eol ?? "\r\n";
  if (options.normalize && (lf > 0 || cr > 0 || lastEol === "")) {
    writer.changes.push({
      kind: "line-endings",
      before: "",
      after: "",
      detail: { count: String(lf + cr + (lastEol === "" ? 1 : 0)) },
    });
  }
  if (!options.normalize && lastEol === "" && writer.out.length > 0) {
    // Keep a file that ended without a line break ending the same way.
    writer.out[writer.out.length - 1] = "";
  }
  writer.changes.sort((a, b) => (a.line ?? 0) - (b.line ?? 0));
  return {
    text: `${parse.leadingBom ? BOM : ""}${writer.out.join("")}`,
    changes: writer.changes,
    records: parse.records.length - plan.drop.size,
  };
}

/* ------------------------------------------------------------- encoding */

export type RisEncoding = "utf-8" | "utf-16le" | "utf-16be" | "windows-1252";

/**
 * Decode an uploaded file. UTF-16 is recognised by its byte-order mark; anything that
 * is not valid UTF-8 is read as Windows-1252, the "ANSI" character set the RIS
 * specification names. A UTF-8 byte-order mark is kept in the text so the check can
 * report it.
 */
export function decodeRisBytes(bytes: Uint8Array): {
  text: string;
  encoding: RisEncoding;
} {
  if (bytes[0] === 0xff && bytes[1] === 0xfe) {
    return {
      text: new TextDecoder("utf-16le").decode(bytes),
      encoding: "utf-16le",
    };
  }
  if (bytes[0] === 0xfe && bytes[1] === 0xff) {
    return {
      text: new TextDecoder("utf-16be").decode(bytes),
      encoding: "utf-16be",
    };
  }
  try {
    return {
      text: new TextDecoder("utf-8", { fatal: true, ignoreBOM: true }).decode(
        bytes
      ),
      encoding: "utf-8",
    };
  } catch {
    return {
      text: new TextDecoder("windows-1252").decode(bytes),
      encoding: "windows-1252",
    };
  }
}
